import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import type { ReferralJobTitle, SaveReferralJobTitleRequest } from '../../api/types';
import { useHubContext } from '../../shell/HubContext';
import { MoneyInput } from './ReferralFormDialog';
import { EditIcon } from './RowIcons';
import { formatMoney } from './referralDates';

/** A row being edited, or the blank one at the bottom that adds a title. */
type Draft = { id: string | null; title: string; defaultPayoutAmount: string; isActive: boolean };

const BLANK: Draft = { id: null, title: '', defaultPayoutAmount: '', isActive: true };

/** The column the list is ordered by. */
type SortKey = 'title' | 'defaultPayoutAmount' | 'isActive';

type StatusFilter = 'all' | 'active' | 'retired';

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
];

/**
 * What a referral for a given job title pays. Entering a referral looks the title up here and fills
 * the amount in, which stays editable on the referral itself — so this is the starting point, not a
 * rule. Small enough to edit in place rather than through a dialog.
 */
export function ReferralJobTitlesSection() {
  const { api } = useHubContext();

  const [jobTitles, setJobTitles] = useState<ReferralJobTitle[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Title ascending is the order the API already hands back, so the page opens on the order it had.
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDescending, setSortDescending] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const { jobTitles: loaded } = await api.listReferralJobTitles(signal);
      setJobTitles(loaded);
    },
    [api],
  );

  useEffect(() => {
    const controller = new AbortController();

    load(controller.signal).catch((error: unknown) => {
      if (controller.signal.aborted) return;

      setLoadError(error instanceof Error ? error.message : 'The job titles could not be loaded.');
    });

    return () => controller.abort();
  }, [load]);

  const visible = useMemo(() => {
    if (jobTitles === null) return [];

    const term = search.trim().toLowerCase();

    const matching = jobTitles.filter((jobTitle) => {
      // The row being edited stays on screen whatever the filters say. Retiring a title from the open
      // row, or typing a letter more into the search box, would otherwise pull a half-finished edit
      // out from under whoever is making it, and those changes have nowhere to go once the row goes.
      if (draft !== null && draft.id === jobTitle.id) return true;

      if (statusFilter === 'active' && !jobTitle.isActive) return false;
      if (statusFilter === 'retired' && jobTitle.isActive) return false;

      return term === '' || jobTitle.title.toLowerCase().includes(term);
    });

    return [...matching].sort((a, b) => {
      const result =
        sortKey === 'defaultPayoutAmount'
          ? // Subtracted, not compared as text: as strings "900" sorts above "1200".
            a.defaultPayoutAmount - b.defaultPayoutAmount
          : sortKey === 'isActive'
            ? Number(b.isActive) - Number(a.isActive)
            : a.title.localeCompare(b.title);

      // Payout and status both tie constantly — a dozen titles on the same amount, and only two
      // statuses in the whole table. Left there, tied rows come back in whatever order the filter
      // happened to produce and look like they shuffle themselves; the title settles them.
      return (sortDescending ? -result : result) || a.title.localeCompare(b.title);
    });
  }, [jobTitles, draft, search, statusFilter, sortKey, sortDescending]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDescending((d) => !d);
    } else {
      setSortKey(key);
      setSortDescending(false);
    }
  }

  function edit(jobTitle: ReferralJobTitle | null) {
    setSaveError(null);
    setFieldErrors({});
    setDraft(
      jobTitle === null
        ? BLANK
        : {
            id: jobTitle.id,
            title: jobTitle.title,
            defaultPayoutAmount: String(jobTitle.defaultPayoutAmount),
            isActive: jobTitle.isActive,
          },
    );
  }

  async function save() {
    if (draft === null) return;

    const request: SaveReferralJobTitleRequest = {
      title: draft.title.trim(),
      defaultPayoutAmount: Number(draft.defaultPayoutAmount),
      isActive: draft.isActive,
    };

    setSaving(true);
    setSaveError(null);
    setFieldErrors({});

    try {
      if (draft.id === null) {
        await api.createReferralJobTitle(request);
      } else {
        await api.updateReferralJobTitle(draft.id, request);
      }

      await load();
      setDraft(null);
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        setSaveError(Object.keys(error.fieldErrors).length > 0 ? null : error.message);
      } else {
        setSaveError(error instanceof Error ? error.message : 'It could not be saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loadError !== null) {
    return (
      <div className="page-message page-message--error">
        <h2>The job titles could not be loaded</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (jobTitles === null) {
    return <div className="page-message">Loading job titles…</div>;
  }

  const addingNew = draft !== null && draft.id === null;

  return (
    <div className="section-page">
      <header className="section-page__header">
        <div>
          <h1>Job titles and payouts</h1>
          <p className="section-page__lead">
            What a referral for each title is worth. The amount fills itself in when a referral is
            entered, and can be changed on that referral without changing it here.
          </p>
        </div>

        <button
          type="button"
          className="button button--primary"
          onClick={() => edit(null)}
          disabled={addingNew}
        >
          Add job title
        </button>
      </header>

      <div className="section-page__toolbar">
        <input
          className="control section-page__search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search job titles"
          aria-label="Search job titles"
        />

        <div className="section-page__chips" role="group" aria-label="Filter by status">
          {statusFilters.map((option) => (
            <button
              key={option.value}
              type="button"
              className={
                statusFilter === option.value
                  ? 'button button--quiet button--small is-selected'
                  : 'button button--quiet button--small'
              }
              aria-pressed={statusFilter === option.value}
              onClick={() => setStatusFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {visible.length < jobTitles.length && (
          <span className="section-page__count">
            {visible.length} of {jobTitles.length}
          </span>
        )}
      </div>

      {saveError !== null && <p className="dialog__error">{saveError}</p>}

      <div className="table-scroll">
        <table className="table table--job-titles">
          <thead>
            <tr>
              <SortableHeader
                label="Job title"
                columnKey="title"
                activeKey={sortKey}
                descending={sortDescending}
                onSort={toggleSort}
              />
              <SortableHeader
                label="Payout"
                columnKey="defaultPayoutAmount"
                className="table__number"
                activeKey={sortKey}
                descending={sortDescending}
                onSort={toggleSort}
              />
              <SortableHeader
                label="Status"
                columnKey="isActive"
                activeKey={sortKey}
                descending={sortDescending}
                onSort={toggleSort}
              />
              <th scope="col" className="table__fit">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((jobTitle) =>
              draft !== null && draft.id === jobTitle.id ? (
                <DraftRow
                  key={jobTitle.id}
                  draft={draft}
                  saving={saving}
                  fieldErrors={fieldErrors}
                  onChange={setDraft}
                  onSave={save}
                  onCancel={() => setDraft(null)}
                />
              ) : (
                <tr key={jobTitle.id} className={jobTitle.isActive ? undefined : 'table__row--done'}>
                  <td className="table__strong">{jobTitle.title}</td>
                  <td className="table__number">
                    {/* A title nobody has priced yet reads as a perfectly ordinary $0 otherwise,
                        which is how referrals get entered against one and come out worth nothing.
                        The tag says the amount is missing rather than settled. */}
                    {jobTitle.defaultPayoutAmount === 0 ? (
                      <span
                        className="badge badge--warn"
                        title="No payout amount has been set for this title"
                      >
                        No payout
                      </span>
                    ) : (
                      formatMoney(jobTitle.defaultPayoutAmount)
                    )}
                  </td>
                  <td>
                    {jobTitle.isActive ? (
                      "Active"
                    ) : (
                      <span className="badge badge--muted">Retired</span>
                    )}
                  </td>
                  <td>
                    <div className="table__actions">
                      <button
                        type="button"
                        className="icon-button"
                        title="Edit this job title"
                        aria-label={"Edit " + jobTitle.title}
                        onClick={() => edit(jobTitle)}
                        disabled={draft !== null}
                      >
                        <EditIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ),
            )}

            {visible.length === 0 && !addingNew && (
              <tr>
                <td colSpan={4} className="table__empty">
                  {jobTitles.length === 0
                    ? 'No job titles yet. Add the first one.'
                    : 'No job titles match that.'}
                </td>
              </tr>
            )}

            {draft !== null && draft.id === null && (
              <DraftRow
                draft={draft}
                saving={saving}
                fieldErrors={fieldErrors}
                onChange={setDraft}
                onSave={save}
                onCancel={() => setDraft(null)}
              />
            )}
          </tbody>
        </table>
      </div>

      <p className="section-page__footnote">
        Retiring a title hides it from the entry form. Referrals already recorded against it keep the
        title they were entered with.
      </p>
    </div>
  );
}

interface SortableHeaderProps {
  label: string;
  columnKey: SortKey;
  className?: string;
  activeKey: SortKey;
  descending: boolean;
  onSort: (key: SortKey) => void;
}

/**
 * A column heading that sorts by its own column.
 *
 * Every sortable column carries an arrow, not only the one in force: a mark that appears once you
 * have already clicked tells you nothing about the columns you have not tried. The idle ones sit back
 * in grey, and the column actually sorting shows its direction.
 *
 * Written against `.table` rather than borrowing the review dashboards' `.care-table__sort`, whose
 * headings are set in ink at body case — on this family's muted small caps it would have left the
 * sortable columns darker than the one beside them.
 */
function SortableHeader({
  label,
  columnKey,
  className,
  activeKey,
  descending,
  onSort,
}: SortableHeaderProps) {
  const isActive = activeKey === columnKey;

  return (
    <th
      scope="col"
      className={className}
      aria-sort={isActive ? (descending ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        className="table__sort"
        onClick={() => onSort(columnKey)}
        title={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        <span
          className={isActive ? 'table__sort-arrow' : 'table__sort-arrow table__sort-arrow--idle'}
          aria-hidden="true"
        >
          {isActive ? (descending ? '▾' : '▴') : '⇅'}
        </span>
      </button>
    </th>
  );
}

function DraftRow(props: {
  draft: Draft;
  saving: boolean;
  fieldErrors: Record<string, string[]>;
  onChange: (draft: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { draft, saving, fieldErrors, onChange, onSave, onCancel } = props;

  return (
    <tr className="table__row--editing">
      <td>
        <input
          className="control"
          value={draft.title}
          onChange={(e) => onChange({ ...draft, title: e.target.value })}
          placeholder="Job title"
          autoFocus
        />
        {fieldErrors.title?.[0] !== undefined && <p className="field__error">{fieldErrors.title[0]}</p>}
      </td>

      <td className="table__number">
        <MoneyInput
          value={draft.defaultPayoutAmount}
          invalid={fieldErrors.defaultPayoutAmount?.[0] !== undefined}
          onChange={(value) => onChange({ ...draft, defaultPayoutAmount: value })}
        />
        {fieldErrors.defaultPayoutAmount?.[0] !== undefined && (
          <p className="field__error">{fieldErrors.defaultPayoutAmount[0]}</p>
        )}
      </td>

      <td>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(e) => onChange({ ...draft, isActive: e.target.checked })}
          />
          <span>Offer this title when entering a referral</span>
        </label>
      </td>

      <td>
        <div className="table__actions">
          <button type="button" className="button button--quiet button--small" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="button button--primary button--small" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </td>
    </tr>
  );
}
